import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, MapPin, Clock, Users, TrendingUp, Award, Send, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';

export const Careers = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Application submitted:', formData);
    toast.success('Thank you for your application! We will review and contact you soon.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      position: '',
      experience: '',
      message: ''
    });
  };

  const currentOpenings = [
    {
      title: 'Electrical Engineer (Site)',
      location: 'Karnataka / Maharashtra',
      type: 'Full-time',
      experience: '2-5 years',
      description: 'Looking for experienced electrical engineers for on-site EPC project execution and supervision.'
    },
    {
      title: 'Engineer - Projects',
      location: 'Bangalore',
      type: 'Full-time',
      experience: '5+ years',
      description: 'Experienced engineer to lead end-to-end EPC electrical projects with P&L responsibility.'
    },
    {
      title: 'Assistant Manager – Business Development (Electrical EPC)',
      location: 'Bangalore',
      type: 'Full-time',
      experience: '3 to 5 years',
      description: 'BE/B.Tech in Electrical Engineering (Preferred). Diploma in Electrical Engineering with relevant EPC experience may also be considered.',
      isDetailed: true,
      qualification: 'BE/B.Tech in Electrical Engineering (Preferred). Diploma in Electrical Engineering with relevant EPC experience may also be considered.',
      responsibilities: {
        businessDev: [
          'Identify and develop new business opportunities in Electrical, Renewable Energy, Substation, Transmission Line, and Industrial Projects',
          'Meet prospective clients, consultants, EPC contractors, and project developers',
          'Generate enquiries, RFQs, and tender opportunities',
          'Prepare techno-commercial proposals, quotations, and bid submissions',
          'Conduct regular follow-ups with customers for order closure',
          'Develop and maintain strong client relationships'
        ],
        projectMgmt: [
          'Coordinate with site teams for project planning and execution',
          'Monitor project progress, manpower deployment, and material availability',
          'Attend project review meetings with clients and stakeholders',
          'Support billing, measurement sheets, and project documentation',
          'Ensure timely completion of projects while maintaining quality and safety standards',
          'Coordinate with vendors, subcontractors, and suppliers'
        ]
      }
    }
  ];

  const benefits = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Career Growth',
      description: 'Structured career path with learning opportunities'
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Team Environment',
      description: 'Collaborative culture with experienced professionals'
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: 'Recognition',
      description: 'Performance-based rewards and recognition'
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: 'Diverse Projects',
      description: 'Work on challenging projects across India'
    }
  ];

  const lifeAtCalspark = [
    { image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=300&fit=crop', title: 'Site Execution' },
    { image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=400&h=300&fit=crop', title: 'Team Collaboration' },
    { image: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=300&fit=crop', title: 'Engineering Excellence' },
    { image: 'https://images.unsplash.com/photo-1509390288171-ce2088f7d08e?w=400&h=300&fit=crop', title: 'Project Commissioning' }
  ];

  return (
    <div className="min-h-screen pt-32 pb-20">
      <SEO 
        title="Careers | Calspark Electrical & Engineers"
        description="Join Calspark Electrical & Engineers. We are hiring Engineers and EPC professionals for electrical, instrumentation and power infrastructure projects across Pan India."
        keywords="electrical engineer jobs India, EPC jobs Karnataka, power sector careers, engineer projects job, business development EPC"
        ogUrl="/careers"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-800 to-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Join Our Team</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Be part of a growing team delivering excellence in EPC electrical projects across India
          </p>
        </div>
      </section>

      {/* Why Join Us with background gradient */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">Why Work With Us?</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Join a team committed to excellence, safety, and professional growth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-slate-100"
              >
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center text-teal-600 mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-3">{benefit.title}</h3>
                <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Openings */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">Current Openings</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Explore opportunities to grow your career with us
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {currentOpenings.map((job, index) => (
              <div
                key={index}
                className={`bg-white border border-slate-200 rounded-xl p-6 hover:shadow-xl transition-all duration-300 hover:border-teal-300 ${job.isDetailed ? 'lg:col-span-2' : ''}`}
              >
                <h3 className="text-xl font-semibold text-slate-800 mb-3">{job.title}</h3>
                <div className="flex flex-wrap gap-4 mb-4">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <MapPin className="w-4 h-4 text-teal-600" />
                    <span>{job.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Clock className="w-4 h-4 text-teal-600" />
                    <span>Experience: {job.experience}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Briefcase className="w-4 h-4 text-teal-600" />
                    <span>{job.type}</span>
                  </div>
                </div>
                
                {job.isDetailed ? (
                  <>
                    <div className="mb-4">
                      <p className="text-slate-600 text-sm mb-2"><strong>Educational Qualification:</strong> {job.qualification}</p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-teal-600" />
                          Business Development
                        </h4>
                        <ul className="space-y-2">
                          {job.responsibilities.businessDev.map((item, idx) => (
                            <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                              <span className="text-teal-600 mt-1">•</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                          <Briefcase className="w-5 h-5 text-teal-600" />
                          Project Management & Execution
                        </h4>
                        <ul className="space-y-2">
                          {job.responsibilities.projectMgmt.map((item, idx) => (
                            <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                              <span className="text-teal-600 mt-1">•</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-slate-600 text-sm mb-6">{job.description}</p>
                )}
                
                <Link
                  to="/contact"
                  onClick={() => window.scrollTo(0, 0)}
                  className="inline-flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-teal-700 transition-colors text-sm"
                >
                  Apply Now <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Life at Calspark */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">Life at CALSPARK</h2>
            <p className="text-lg text-slate-600 max-w-4xl mx-auto leading-relaxed">
              At Calspark, we believe in building careers as strong as the infrastructure we deliver. Our team thrives on challenging projects, collaborative culture, and the pride of powering India's growth. We offer real-world EPC exposure, professional growth, and the opportunity to work on high-voltage projects across Pan India.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {lifeAtCalspark.map((item, index) => (
              <div key={index} className="relative group overflow-hidden rounded-xl">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent flex items-end">
                  <p className="text-white font-medium p-4">{item.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="application-form" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
            <h2 className="text-3xl font-bold text-slate-800 mb-6">Submit Your Application</h2>
            <p className="text-slate-600 mb-8">
              Fill out the form below and we'll get back to you within 48 hours.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                    placeholder="Your phone"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                  placeholder="your@email.com"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="position" className="block text-sm font-medium text-slate-700 mb-2">
                    Position Applied For *
                  </label>
                  <input
                    type="text"
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                    placeholder="e.g., Electrical Engineer"
                  />
                </div>

                <div>
                  <label htmlFor="experience" className="block text-sm font-medium text-slate-700 mb-2">
                    Years of Experience *
                  </label>
                  <input
                    type="text"
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                    placeholder="e.g., 5 years"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                  Cover Letter / Additional Information *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors resize-none"
                  placeholder="Tell us about your experience and why you'd like to join our team..."
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-teal-600 text-white px-6 py-4 rounded-lg font-medium hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                <Send className="w-5 h-5" />
                Submit Application
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Don't See a Perfect Fit?
          </h2>
          <p className="text-xl text-teal-100 mb-4">
            We're always looking for talented individuals. Send us your resume and we'll keep you in mind for future opportunities.
          </p>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8 inline-block">
            <p className="text-lg text-white mb-2">To apply, send your profile to:</p>
            <a
              href="mailto:hr@calsparkengineers.com"
              className="text-2xl font-bold text-white hover:text-teal-200 transition-colors"
            >
              hr@calsparkengineers.com
            </a>
          </div>
          <div>
            <a
              href="mailto:hr@calsparkengineers.com"
              className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-medium hover:bg-slate-50 transition-all duration-300 shadow-lg"
            >
              Email Your Resume
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
